// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-134 — Lista compacta de insignias de Gestión. Reemplaza los 5 bloques expandidos que
// antes vivían inline en AdminGestion.tsx: una fila por insignia (miniatura, nombre en el idioma
// activo de la UI, semanas, color, estado, botón Editar), más "+ Agregar insignia". La edición
// completa vive en AdminBadgeEditorModal — esta lista solo resume y despacha.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBadgeName } from '@/api/recognition';
import type { AdminBadgeDto } from '@/api/recognition';
import { ConfirmModal } from '@/components/common';
import { hasAllLanguages } from './adminShared';
import { weeksKeyOf } from '@/utils/weeksPlural';
import { asset } from '@/utils/basePath';

const ADMIN_WEEKS_KEYS = {
  one: 'admin.management.weeksRequiredOne',
  few: 'admin.management.weeksRequired',
  many: 'admin.management.weeksRequiredMany',
} as const;

interface AdminBadgeListProps {
  badges: AdminBadgeDto[];
  onAdd: () => void;
  onEdit: (badge: AdminBadgeDto) => void;
  /** CPE-150 — quita la insignia del borrador; el borrado real ocurre al Guardar (PUT atómico). */
  onDelete: (badge: AdminBadgeDto) => void;
}

export function AdminBadgeList({ badges, onAdd, onEdit, onDelete }: Readonly<AdminBadgeListProps>) {
  const { t, i18n } = useTranslation('recognition');
  const [pendingDelete, setPendingDelete] = useState<AdminBadgeDto | null>(null);

  return (
    <div className="adm-field">
      <div className="adm-badge-list-head">
        <span>{t('admin.management.badges')}</span>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onAdd}>
          <i className="fas fa-plus" /> {t('admin.management.addBadge')}
        </button>
      </div>

      {badges.length === 0 && <p className="adm-note">{t('admin.management.noBadges')}</p>}

      <ul className="adm-badge-list">
        {badges.map((badgeItem) => {
          const weeksLabel = typeof badgeItem.semanasRequeridas === 'number'
            ? t(weeksKeyOf(badgeItem.semanasRequeridas, ADMIN_WEEKS_KEYS), { count: badgeItem.semanasRequeridas })
            : '';
          const incompleteLangs = !hasAllLanguages(badgeItem.nombre);
          return (
            <li key={badgeItem.id} className="adm-badge-row">
              <img src={badgeItem.iconUrl || asset('/color-icon.png')} alt="" className="adm-badge-row-thumb" />
              <div className="adm-badge-row-meta">
                <span className="adm-badge-row-name">{getBadgeName(badgeItem, i18n.language)}</span>
                {weeksLabel && <span className="adm-badge-row-weeks">{weeksLabel}</span>}
                {incompleteLangs && (
                  <span className="adm-note adm-note--warn"><i className="fas fa-language" /> {t('admin.management.needAllLangs')}</span>
                )}
              </div>
              <span className="adm-color-swatch" style={{ background: badgeItem.color ?? 'transparent' }} />
              <span className={`adm-badge-status ${badgeItem.isActive ? 'adm-badge-status--on' : 'adm-badge-status--off'}`}>
                {badgeItem.isActive ? t('admin.management.badgeActive') : t('admin.management.badgeInactive')}
              </span>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => onEdit(badgeItem)}>
                {t('admin.management.editBadge')}
              </button>
              {/* CPE-150 — la papelera de la maqueta (`fundamentales-admin.js:1163`), que hasta hoy
                  no existía en la pantalla. Quita la fila del BORRADOR; nada se borra hasta Guardar,
                  igual que el resto de esta pantalla. Se pide confirmación porque una insignia
                  borrada se lleva su nombre en 7 idiomas, su icono y su umbral, y rehacerla a mano
                  no es un par de clics. */}
              <button
                type="button"
                className="btn-small btn-danger-sm"
                title={t('admin.management.deleteBadge')}
                aria-label={`${t('admin.management.deleteBadge')}: ${getBadgeName(badgeItem, i18n.language)}`}
                onClick={() => setPendingDelete(badgeItem)}
              >
                <i className="fas fa-trash" />
              </button>
            </li>
          );
        })}
      </ul>

      {pendingDelete && (
        <ConfirmModal
          title={t('admin.management.deleteBadge')}
          message={t('admin.management.deleteBadgeConfirm', {
            name: getBadgeName(pendingDelete, i18n.language),
          })}
          confirmText={t('admin.management.deleteBadge')}
          onConfirm={() => onDelete(pendingDelete)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

export default AdminBadgeList;
